// src/components/admin/ProductProfile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ImageIcon from '@mui/icons-material/Image';
import { AdminShell } from './Sidebar';
import { LoadingState } from '../ui/CrmPage';

const currency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 }).format(n) + ' ₸';
};

const normalizeCategories = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((c) => ({
      id: c.id ?? c.id_product_category ?? c.category_id ?? c.value ?? c.code ?? '',
      name: c.name ?? c.category_name ?? c.label ?? '',
    }))
    .filter((c) => c.id !== '' && c.name !== '');

export default function ProductProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const [categories, setCategories] = useState([]);
  const [measures, setMeasures] = useState([]);

  const [product, setProduct] = useState({
    id: '', name: '',
    id_product_category: '', category_name: '',
    id_measure: '', description: '', price: '', product_photo: '',
    manufacturer_country: '', type_of_packaging: '', storage_conditions: '',
    brand: '', shelf_life: '', guid: '', article: '',
  });

  const categoryIdByName = (cname) => categories.find((c) => c.name === cname)?.id ?? '';

  useEffect(() => {
    const ctrl = new AbortController();
    async function softFetch(url) {
      try {
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error();
        return await r.json();
      } catch { return []; }
    }
    (async () => {
      try {
        setLoading(true); setError('');
        const res = await fetch(`${apiBase}v1/products/${id}`, { signal: ctrl.signal });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Products HTTP ${res.status} ${res.statusText} ${t}`.trim());
        }
        const p = await res.json();
        const [catsRaw, measRaw] = await Promise.all([
          softFetch(`${apiBase}v1/productCategories`),
          softFetch(`${apiBase}v1/measures?sortBy=id&orderBy=asc`),
        ]);
        const cats = normalizeCategories(catsRaw);
        setCategories(cats);
        setMeasures(Array.isArray(measRaw) ? measRaw : []);
        const cname = p.category_name
          ?? (p.id_product_category
            ? cats.find((c) => String(c.id) === String(p.id_product_category))?.name
            : '')
          ?? '';
        setProduct({
          id: String(p.id ?? id),
          name: p.name ?? '',
          id_product_category: p.id_product_category ?? '',
          category_name: cname || '',
          id_measure: p.id_measure ?? '',
          description: p.description ?? '',
          price: p.price ?? '',
          product_photo: p.product_photo ?? '',
          manufacturer_country: p.manufacturer_country ?? '',
          type_of_packaging: p.type_of_packaging ?? '',
          storage_conditions: p.storage_conditions ?? '',
          brand: p.brand ?? '',
          shelf_life: p.shelf_life ?? '',
          guid: p.guid ?? '',
          article: p.article ?? '',
        });
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category_name') {
      const newId = categoryIdByName(value);
      setProduct((s) => ({ ...s, category_name: value, id_product_category: newId ? String(newId) : '' }));
      return;
    }
    setProduct((s) => ({ ...s, [name]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(''); setSaveOk('');
    try {
      const body = {
        name: product.name,
        category_name: product.category_name || null,
        id_product_category: product.id_product_category === '' ? null : Number(product.id_product_category),
        id_measure: product.id_measure === '' ? null : Number(product.id_measure),
        description: product.description,
        price: product.price === '' || product.price === null ? null : Number(product.price),
        product_photo: product.product_photo || null,
        manufacturer_country: product.manufacturer_country || null,
        type_of_packaging: product.type_of_packaging || null,
        storage_conditions: product.storage_conditions || null,
        brand: product.brand || null,
        shelf_life: product.shelf_life || null,
        guid: product.guid || null,
        article: product.article || null,
      };
      const res = await fetch(`${apiBase}v1/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Save: HTTP ${res.status} ${res.statusText} ${t}`.trim());
      }
      setSaveOk('Изменения сохранены');
    } catch (e) {
      setSaveError(e.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Карточка товара" subtitle="Загрузка…">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <LoadingState text="Загружаем данные товара…" />
          </div>
        </div>
      </AdminShell>
    );
  }
  if (error) {
    return (
      <AdminShell title="Карточка товара" subtitle="Ошибка">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <div className="crmpage__alert crmpage__alert--error">{error}</div>
            <div className="adminform__actions">
              <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
            </div>
          </div>
        </div>
      </AdminShell>
    );
  }

  const hasPhoto = !!(product.product_photo && String(product.product_photo).trim());

  return (
    <AdminShell
      breadcrumbs="Товары"
      title={product.name || 'Карточка товара'}
      subtitle={`ID: ${product.id}`}
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
          <button type="button" className="adminbtn adminbtn--primary" disabled={saving} onClick={onSave}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!saveError && <div className="crmpage__alert crmpage__alert--error">{saveError}</div>}
          {!!saveOk && <div className="crmpage__alert" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: '#0F5B3C' }}>{saveOk}</div>}

          <div className="adminprofile__header">
            <div className="adminprofile__avatar" style={{ width: 72, height: 72, padding: 0, overflow: 'hidden' }}>
              {hasPhoto ? (
                <img
                  alt={product.name}
                  src={product.product_photo}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <ImageIcon sx={{ fontSize: 28, color: '#fff' }} />
              )}
            </div>
            <div className="adminprofile__head-text">
              <h2 className="adminprofile__name">{product.name || 'Без названия'}</h2>
              <div className="adminprofile__meta">
                <span>Категория: {product.category_name || '—'}</span>
                <span className="adminprofile__meta-sep" />
                <span>{product.brand || 'Бренд не указан'}</span>
                <span className="adminprofile__meta-sep" />
                <span className="crmpage__status crmpage__status--brand">{currency(product.price)}</span>
              </div>
            </div>
          </div>

          <form className="adminform" onSubmit={onSave}>
            <section className="adminform__card">
              <div className="adminform__section-title">Основное</div>
              <div className="adminform__grid">
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Название<span className="req">*</span></span>
                  <input className="adminform__input" name="name" value={product.name} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Категория</span>
                  {categories.length > 0 ? (
                    <select className="adminform__select" name="category_name" value={product.category_name} onChange={onChange}>
                      <option value="">— выберите —</option>
                      {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input className="adminform__input" name="category_name" value={product.category_name} onChange={onChange} />
                  )}
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Ед. измерения</span>
                  {measures.length > 0 ? (
                    <select className="adminform__select" name="id_measure" value={product.id_measure} onChange={onChange}>
                      <option value="">— выберите —</option>
                      {measures.map((m) => <option key={m.id} value={String(m.id)}>{m.name ?? `#${m.id}`}</option>)}
                    </select>
                  ) : (
                    <input type="number" className="adminform__input" name="id_measure" value={product.id_measure} onChange={onChange} placeholder="ID единицы" />
                  )}
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Цена, ₸</span>
                  <input type="number" className="adminform__input" name="price" value={product.price} onChange={onChange} step="0.01" />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">URL фото</span>
                  <input className="adminform__input" name="product_photo" value={product.product_photo} onChange={onChange} placeholder="https://…" />
                </label>
              </div>
            </section>

            <section className="adminform__card">
              <div className="adminform__section-title">Атрибуты</div>
              <div className="adminform__grid">
                <label className="adminform__field">
                  <span className="adminform__label">Бренд</span>
                  <input className="adminform__input" name="brand" value={product.brand} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Артикул</span>
                  <input className="adminform__input" name="article" value={product.article} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">GUID</span>
                  <input className="adminform__input" name="guid" value={product.guid} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Страна-производитель</span>
                  <input className="adminform__input" name="manufacturer_country" value={product.manufacturer_country} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Тип упаковки</span>
                  <input className="adminform__input" name="type_of_packaging" value={product.type_of_packaging} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Условия хранения</span>
                  <input className="adminform__input" name="storage_conditions" value={product.storage_conditions} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Срок годности</span>
                  <input className="adminform__input" name="shelf_life" value={product.shelf_life} onChange={onChange} placeholder="например, 12 месяцев" />
                </label>
              </div>
            </section>

            <section className="adminform__card">
              <div className="adminform__section-title">Описание</div>
              <label className="adminform__field">
                <textarea className="adminform__textarea" name="description" value={product.description} onChange={onChange} rows={6} />
              </label>
            </section>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
